import React, { useEffect, useState } from 'react'
import { Modal, Input, Form, Cascader, Radio, Select } from 'antd'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useForm } from 'antd/lib/form/Form'
import { CustomReport } from '../../../../redux/context/customReports/types'
import { currentUserSortedCompaniesSelector } from '../../../../redux/session/currentUser/selectors'
import { useBackend } from '../../../../services/backend'
import useCtxCopyCustomReport from '../../../../features/customReport/queries/report/useCtxCopyCustomReport'
import { useCtxCreateCustomReport } from '../../../../features/customReport/queries/report/useCtxCreateCustomReport'
import { useCtxUpdateCustomReport } from '../../../../features/customReport/queries/report/useCtxUpdateCustomReport'
import { useResetFormOnCloseModal } from '../../../../components/Form/hooks'
import TranslateFields, { languageFields } from '../../../../components/Form/TranslateFields'
import { getTitleByLanguage } from './customReportTable/utils'
import { useCalculatedReportContext } from './CalculatedReportContext'

interface CustomReportFormProps {
  report?: CustomReport | null
  visible: boolean
  onClose: () => void
}

const CustomReportModal: React.FC<CustomReportFormProps> = ({ visible, report, onClose }: CustomReportFormProps) => {
  const { t } = useTranslation()
  const userCompanies = useSelector(currentUserSortedCompaniesSelector)
  const [companies, setCompanies] = useState(userCompanies.map(({ id, name }) => ({ id, title: name, isLeaf: false })))
  const reportRequest = useBackend(`/api/companies/{companyId}/reporting/custom/reports`)
  const [reportForm] = useForm<Partial<CustomReport>>()
  const [newReportMode, setNewReportMode] = useState<'new' | 'copy'>('new')
  const [copySelection, setCopySelection] = useState<(string | number)[]>([])
  const [confirmLoading, setConfirmLoading] = useState(false)

  const { mutateAsync: copyCustomReport } = useCtxCopyCustomReport()
  const { mutateAsync: createCustomReport } = useCtxCreateCustomReport()
  const { mutateAsync: updateCustomReport } = useCtxUpdateCustomReport()
  const { setReportId } = useCalculatedReportContext()

  useResetFormOnCloseModal({ form: reportForm, open: visible })

  useEffect(() => {
    if (report && visible) {
      const translationFields = Object.values(languageFields).reduce((acc, fieldName) => {
        const value = report[fieldName]
        if (value) {
          acc[fieldName] = value
        }
        return acc
      }, {} as Record<string, string>)

      reportForm.setFieldsValue({
        id: report.id,
        name: report.name,
        style: report.style,
        ...translationFields
      })
    }
  }, [report, visible, reportForm])

  const handleCancel = () => {
    onClose()
    setNewReportMode('new')
  }

  const handleSave = async () => {
    setConfirmLoading(true)
    try {
      const [, reportId] = copySelection
      switch (newReportMode) {
        case 'copy': {
          const copiedReport = await copyCustomReport(reportId)
          setReportId(copiedReport.id)
          onClose()
          break
        }
        case 'new': {
          const values = await reportForm.validateFields()
          if (values.id) {
            await updateCustomReport({ data: values })
          } else {
            const createdReport = await createCustomReport(values)
            setReportId(createdReport.id)
          }
          onClose()
          break
        }
        default:
          break
      }
      setNewReportMode('new')
    } catch {
      // Form validation errors highlight fields in the UI; mutation errors are surfaced by their own onError handlers
    } finally {
      setConfirmLoading(false)
    }
  }

  const loadData = async (selectedOptions: any) => {
    const targetOption = selectedOptions[selectedOptions.length - 1]
    targetOption.loading = true
    try {
      const response = await reportRequest.get({ urlParams: { companyId: targetOption.id } })
      targetOption.loading = false

      targetOption.children =
        response.length === 0
          ? [{ name: t('global:no-results'), id: null, isLeaf: true, disabled: true }]
          : response.map((r: CustomReport) => ({ ...r, title: getTitleByLanguage(r) }))
      setCompanies([...companies])
    } catch (error) {
      targetOption.loading = false
      console.log(error)
    }
  }

  const onChange = (value: any) => {
    setCopySelection(value)
  }

  const filter = (inputValue: any, path: any) => {
    if (
      path.find((option: any) => option?.title?.toLowerCase().includes(inputValue?.toLowerCase())) &&
      inputValue.length > 2
    ) {
      const selectedCompany: any = companies?.find(
        c => c.id === path.find((option: any) => option?.title?.toLowerCase().includes(inputValue?.toLowerCase()))?.id
      )
      if (selectedCompany && (!selectedCompany.children || selectedCompany.children.length < 1)) {
        loadData([path.find((option: any) => option?.title?.toLowerCase().includes(inputValue?.toLowerCase()))])
      }
    }
    return path.some((option: any) => option?.title?.toLowerCase().indexOf(inputValue?.toLowerCase()) > -1)
  }

  return (
    <Modal
      data-cy="report-modal"
      destroyOnHidden
      open={visible}
      title={report?.id ? t('customReportPage:edit-report') : t('customReportPage:new-report')}
      okText={t('global:save')}
      cancelText={t('global:cancel')}
      onCancel={handleCancel}
      maskClosable={false}
      onOk={handleSave}
      okButtonProps={{
        disabled: copySelection && copySelection.length < 2 && newReportMode === 'copy'
      }}
      confirmLoading={confirmLoading}
    >
      {!report?.id && (
        <Radio.Group
          data-cy="report-mode-radio"
          onChange={e => {
            e.preventDefault()
            setNewReportMode(e.target.value)
          }}
          value={newReportMode}
          style={{ marginBottom: 16 }}
        >
          <Radio value="new">{t('contractsPage:add-new')}</Radio>
          <Radio value="copy">{t('global:copy', { what: '' })}</Radio>
        </Radio.Group>
      )}
      {newReportMode === 'new' && (
        <Form
          data-cy="report-form"
          form={reportForm}
          layout="vertical"
          initialValues={{ style: { orientation: 'landscape' } }}
        >
          <Form.Item name="id" style={{ display: 'none' }}>
            <Input />
          </Form.Item>

          <Form.Item
            name="name"
            label={t('global:name')}
            rules={[{ required: true, message: t('global:required-field') }]}
          >
            <Input />
          </Form.Item>
          <TranslateFields />
          <Form.Item
            name={['style', 'orientation']}
            label={t('customReportPage:print-orientation')}
            initialValue="landscape"
            rules={[{ required: true, message: t('global:required-field') }]}
          >
            <Select
              defaultValue="landscape"
              style={{ width: 120 }}
              options={[
                { value: 'landscape', label: t('customReportPage:landscape') },
                { value: 'portrait', label: t('customReportPage:portrait') }
              ]}
            />
          </Form.Item>
        </Form>
      )}
      {!report?.id && newReportMode === 'copy' && (
        <Form layout="vertical">
          <Form.Item name="reportId" label={t('global:copy', { what: '' })}>
            <Cascader<any>
              data-cy="report-copy-cascader"
              value={copySelection}
              fieldNames={{ label: 'title', value: 'id', children: 'children' }}
              options={companies}
              placeholder={t('global:choose-here')}
              loadData={loadData}
              onChange={onChange}
              showSearch={{ filter }}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}

export default CustomReportModal
